import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/common/header";
import { GET_USER_BRAND_LIKES, CANCEL_BRAND_LIKE } from "../api/brand-like";
import { GET_CAMPAIGNS } from "../api/campaigns";
import { FaHeart, FaTimes } from "react-icons/fa";
import { notify, notifyError } from "../../../utils/toast";
import { SkeletonWave } from "../../../components/common/skeleton-wave";

const MyBrandLikesPage = () => {
  const navigate = useNavigate();
  const { data, loading } = useQuery(GET_USER_BRAND_LIKES);
  const { data: campaignsData } = useQuery(GET_CAMPAIGNS, {
    variables: { upcomingDays: 30 },
  });

  const [cancelBrandLike] = useMutation(CANCEL_BRAND_LIKE, {
    refetchQueries: [{ query: GET_USER_BRAND_LIKES }],
    onCompleted: () => {
      notify("브랜드 좋아요를 취소했어요");
    },
    onError: (error) => {
      notifyError("좋아요 취소 중 오류가 발생했어요");
      console.error("좋아요 취소 오류:", error);
    },
  });

  const handleCancelLike = async (brandId: number, brandName: string) => {
    if (window.confirm(`${brandName} 좋아요를 취소하시겠어요?`)) {
      try {
        await cancelBrandLike({
          variables: { brandId },
        });
      } catch (error) {
        // 에러는 onError에서 처리됨
      }
    }
  };

  const brandLikes = data?.getUserBrandLikes || [];
  const allCampaigns = campaignsData?.campaigns || [];

  // 브랜드별 진행중인 캠페인 필터링
  const getBrandCampaigns = (brandId: number) => {
    const now = new Date();
    return allCampaigns.filter((campaign: any) => {
      if (campaign.brand.id !== brandId || !campaign.isPublished) {
        return false;
      }
      // 진행중인 run이 있는지 확인
      return campaign.runs?.some((run: any) => {
        if (!run.isActive) return false;
        const runEndAt = new Date(run.endAt);
        return runEndAt > now;
      });
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header title="내 좋아요 매장" showBackButton={true} />
      <div className="max-w-4xl mx-auto p-4">
        {loading ? (
          <SkeletonWave className="pt-4" />
        ) : brandLikes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FaHeart className="text-gray-300 w-16 h-16 mb-4" />
            <p className="text-gray-500 text-lg">좋아요한 브랜드가 없습니다</p>
            <p className="text-gray-400 text-sm mt-2">
              브랜드를 좋아요하면 여기에 표시됩니다
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {brandLikes.map((like: any) => {
              const brandCampaigns = getBrandCampaigns(like.brand.id);

              return (
                <div
                  key={like.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  {/* 타이틀 및 취소 버튼 */}
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FaHeart className="text-red-500 w-5 h-5 fill-current" />
                      <h3 className="font-bold text-lg text-gray-900">
                        {like.brand.name}
                      </h3>
                    </div>
                    <button
                      onClick={() =>
                        handleCancelLike(like.brand.id, like.brand.name)
                      }
                      className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <FaTimes className="w-4 h-4" />
                      <span>취소</span>
                    </button>
                  </div>

                  {/* 주소 */}
                  {like.brand.roadAddr && (
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                      <p className="text-sm text-gray-600">
                        {like.brand.roadAddr}
                      </p>
                    </div>
                  )}

                  {/* 진행중인 캠페인 카드들 */}
                  {brandCampaigns.length > 0 ? (
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        🔥진행중인 미션
                      </h4>
                      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                        {brandCampaigns.map((campaign: any) => (
                          <div
                            key={campaign.id}
                            onClick={() =>
                              navigate(`/campaigns/${campaign.id}`)
                            }
                            className="relative w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
                          >
                            {campaign.productImageUrl ? (
                              <img
                                src={campaign.productImageUrl}
                                alt={campaign.productName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-400 text-xs">
                                  이미지 없음
                                </span>
                              </div>
                            )}
                            {/* 상품명 오버레이 */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
                              <p className="text-white text-xs font-semibold line-clamp-1">
                                {campaign.productName}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <p className="text-gray-400 text-sm">
                        진행중인 캠페인이 없습니다
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBrandLikesPage;
